import React, { useCallback, useEffect, useState } from "react"
import PropTypes from "prop-types"
import dayjs from "dayjs"
import { Bounce, toast } from "react-toastify"
import ListingTabs from "../Listing/ListingTabs"
import SearchInput from "../Listing/SearchInput"
import FilterDropdown from "../Listing/FilterDropdown"
import ExportLogButton from "../Listing/ExportLogButton"
import Pagination from "../Listing/Pagination"
import CustomButton from "../OnboardingComponents/CustomButton"
import CustomIconButton from "../OnboardingComponents/CustomIconButton"
import EmptyState from "../Common/EmptyState"
import TruncatedText from "../Common/TruncatedText"
import AdminCityListingTitle from "../AdminLayout/AdminCityListingTitle"
import DeleteConfirmation from "../Common/DeleteConfirmation"
import { ICONS } from "../../constants/icons"
import { del, get } from "../../api/axios"
import endpoints from "../../api/endpoints"
import { toastApiError } from "../../helpers/apiError"
import useAdminPermission from "../../hooks/useAdminPermission"
import {
  CITY_REVIEW_STATUS_TABS,
  DEFAULT_REVIEW_SORT_BY,
  DEFAULT_REVIEW_SORT_DIRECTION,
  REVIEW_SORT_OPTIONS,
  mapReviewRow,
  mapUiStatusToApi,
} from "../../pages/Admin/Cities/adminCityReviewsData"
import ReviewFormModal from "../../pages/Admin/Cities/ReviewFormModal"
import ReviewDetailModal from "../../pages/Admin/Cities/ReviewDetailModal"

const PAGE_SIZE = 10

const STATUS_LABEL = {
  pending: "Pending",
  approved: "Approved",
  rejected: "Rejected",
}

const getInitials = name =>
  (name || "")
    .split(" ")
    .map(part => part.charAt(0))
    .join("")
    .slice(0, 2)
    .toUpperCase()

/**
 * Shared Reviews listing — Figma City Admin / Manage City Reviews.
 * Live API: GET /api/v1/admin/reviews (optional city_id).
 */
const ReviewsListing = ({
  documentTitle = "Reviews | AMOK . EARTH Admin",
  showExport = true,
  cityId,
  allowCreate = false,
}) => {
  const { can } = useAdminPermission()
  const canCreateReview = allowCreate && can("reviews", "create")
  const canEditReview = allowCreate && can("reviews", "edit")
  const canDeleteReview = allowCreate && can("reviews", "delete")
  const [activeTab, setActiveTab] = useState("all")
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [sortBy, setSortBy] = useState(DEFAULT_REVIEW_SORT_BY)
  const [sortDirection, setSortDirection] = useState(
    DEFAULT_REVIEW_SORT_DIRECTION,
  )
  const [currentPage, setCurrentPage] = useState(1)
  const [rows, setRows] = useState([])
  const [totalItems, setTotalItems] = useState(0)
  const [pageSize, setPageSize] = useState(PAGE_SIZE)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isExporting, setIsExporting] = useState(false)
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [editReviewId, setEditReviewId] = useState(null)
  const [viewReviewId, setViewReviewId] = useState(null)
  const [reviewToDelete, setReviewToDelete] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)

  useEffect(() => {
    document.title = documentTitle
  }, [documentTitle])

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300)
    return () => clearTimeout(timer)
  }, [search])

  useEffect(() => {
    setCurrentPage(1)
  }, [activeTab, debouncedSearch, sortBy, sortDirection, cityId])

  const buildListParams = useCallback(
    ({ includePagination = true } = {}) => {
      const params = {
        sort_by: sortBy,
        sort_direction: sortDirection,
      }
      if (includePagination) {
        params.page = currentPage
        params.page_size = PAGE_SIZE
      }
      if (debouncedSearch) params.search = debouncedSearch
      if (activeTab !== "all") params.status = mapUiStatusToApi(activeTab)
      if (cityId) params.city_id = cityId
      return params
    },
    [activeTab, cityId, currentPage, debouncedSearch, sortBy, sortDirection],
  )

  const fetchReviews = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await get(endpoints.getAdminReviews, {
        params: buildListParams(),
      })
      if (response?.data?.success) {
        const result = response.data.result || {}
        setRows((result.data || []).map(mapReviewRow))
        setTotalItems(result.total || 0)
        setPageSize(result.per_page || PAGE_SIZE)

        const totalPages = Math.max(
          1,
          Math.ceil((result.total || 0) / (result.per_page || PAGE_SIZE)),
        )
        if (currentPage > totalPages) setCurrentPage(totalPages)
      } else {
        const message = toastApiError(response, "Failed to load reviews")
        setError(message)
        setRows([])
        setTotalItems(0)
      }
    } catch (err) {
      const message = toastApiError(err, "Failed to load reviews")
      setError(message)
      setRows([])
      setTotalItems(0)
    } finally {
      setIsLoading(false)
    }
  }, [buildListParams, currentPage])

  useEffect(() => {
    fetchReviews()
  }, [fetchReviews])

  const handleExport = async () => {
    if (isExporting) return
    setIsExporting(true)
    try {
      const response = await get(endpoints.exportAdminReviews, {
        params: buildListParams({ includePagination: false }),
        responseType: "blob",
        skipErrorToast: true,
      })

      if (!response || response.status >= 400) {
        toastApiError(response, "Failed to export reviews")
        return
      }

      const contentType = String(
        response.headers?.["content-type"] || "",
      ).toLowerCase()

      if (contentType.includes("application/json")) {
        const text = await response.data.text()
        let message = "Failed to export reviews"
        try {
          const parsed = JSON.parse(text)
          message = parsed?.message || message
        } catch {
          // keep default
        }
        toast.error(message, {
          position: "bottom-center",
          theme: "colored",
          transition: Bounce,
        })
        return
      }

      const disposition = response.headers?.["content-disposition"] || ""
      const filenameMatch = disposition.match(
        /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i,
      )
      const filename = filenameMatch?.[1]
        ? filenameMatch[1].replace(/['"]/g, "")
        : `reviews-${dayjs().format("YYYY-MM-DD")}.csv`

      const blob =
        response.data instanceof Blob
          ? response.data
          : new Blob([response.data], { type: "text/csv;charset=utf-8;" })
      const url = URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)

      toast.success("Reviews exported.", {
        position: "bottom-center",
        theme: "colored",
        transition: Bounce,
      })
    } catch (err) {
      toastApiError(err, "Failed to export reviews")
    } finally {
      setIsExporting(false)
    }
  }

  const handleView = row => {
    setViewReviewId(row.id)
  }

  const handleEdit = row => {
    setEditReviewId(row.id)
  }

  const handleDelete = row => {
    setReviewToDelete(row)
  }

  const closeDeleteModal = () => {
    if (isDeleting) return
    setReviewToDelete(null)
  }

  const handleDeleteConfirm = async () => {
    if (!reviewToDelete?.id || isDeleting) return
    setIsDeleting(true)
    try {
      const response = await del(
        endpoints.deleteAdminReview(reviewToDelete.id),
        { skipErrorToast: true },
      )
      if (
        !response ||
        response.status >= 400 ||
        response?.data?.success === false
      ) {
        toastApiError(response, "Failed to delete review")
        return
      }
      toast.success(
        response?.data?.message || `"${reviewToDelete.title}" deleted.`,
        {
          position: "bottom-center",
          theme: "colored",
          transition: Bounce,
        },
      )
      setReviewToDelete(null)
      await fetchReviews()
    } catch (err) {
      toastApiError(err, "Failed to delete review")
    } finally {
      setIsDeleting(false)
    }
  }

  const closeFormModal = () => {
    setIsCreateOpen(false)
    setEditReviewId(null)
  }

  return (
    <div className="admin-page admin-city-reviews">
      <div className="admin-city-reviews__toolbar">
        {cityId ? (
          <AdminCityListingTitle label="Reviews" cityId={cityId} />
        ) : (
          <h1 className="admin-city-reviews__title">Reviews</h1>
        )}
        <div className="admin-city-reviews__controls">
          <SearchInput
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by Reviewer..."
            iconSrc={ICONS.callFormalSearchStatus}
            className="admin-city-reviews__search"
          />
          <FilterDropdown
            label="Filter"
            filters={[
              {
                key: "sortBy",
                type: "sortBy",
                label: "Sort By",
                value: sortBy,
                defaultValue: DEFAULT_REVIEW_SORT_BY,
                onChange: next => {
                  setSortBy(next || DEFAULT_REVIEW_SORT_BY)
                },
                options: REVIEW_SORT_OPTIONS,
              },
              {
                key: "sortDirection",
                type: "sortDirection",
                label: "Order",
                value: sortDirection,
                defaultValue: DEFAULT_REVIEW_SORT_DIRECTION,
                onChange: next => {
                  setSortDirection(next || DEFAULT_REVIEW_SORT_DIRECTION)
                },
              },
            ]}
          />
          {showExport ? (
            <ExportLogButton
              entityLabel="reviews"
              onClick={handleExport}
              loading={isExporting}
              disabled={isExporting}
            />
          ) : null}
          {canCreateReview && cityId ? (
            <CustomButton
              variant="primary"
              size="md"
              type="button"
              trailingIconSrc={ICONS.plus}
              trailingIconAlt=""
              onClick={() => setIsCreateOpen(true)}
            >
              Create Review
            </CustomButton>
          ) : null}
        </div>
      </div>

      <div className="admin-city-reviews__tabs-row">
        <ListingTabs
          items={CITY_REVIEW_STATUS_TABS}
          activeKey={activeTab}
          onChange={setActiveTab}
        />
      </div>

      <div className="admin-city-reviews__table-wrapper">
        <div
          className="admin-city-reviews__table"
          role="table"
          aria-busy={isLoading || undefined}
        >
          <div
            className="admin-city-reviews__table-row admin-city-reviews__table-row--head"
            role="row"
          >
            <div role="columnheader">Reviewer</div>
            <div role="columnheader">Review Title</div>
            <div role="columnheader">Date Submitted</div>
            <div role="columnheader">Status</div>
            <div
              role="columnheader"
              className="admin-city-reviews__actions-head"
            >
              Actions
            </div>
          </div>

          {isLoading
            ? Array.from({ length: 6 }).map((_, index) => (
                <div
                  key={`review-skel-${index}`}
                  className="admin-city-reviews__table-row admin-city-reviews__table-row--skeleton"
                  role="row"
                >
                  <div className="admin-city-reviews__skeleton" />
                  <div className="admin-city-reviews__skeleton" />
                  <div className="admin-city-reviews__skeleton" />
                  <div className="admin-city-reviews__skeleton" />
                  <div className="admin-city-reviews__skeleton" />
                </div>
              ))
            : null}

          {!isLoading &&
            rows.map(row => (
              <div
                key={row.id}
                className="admin-city-reviews__table-row"
                role="row"
              >
                <div className="admin-city-reviews__user" role="cell">
                  {row.reviewerImage ? (
                    <img
                      className="admin-city-reviews__avatar admin-city-reviews__avatar--image"
                      src={row.reviewerImage}
                      alt=""
                    />
                  ) : (
                    <div
                      className="admin-city-reviews__avatar"
                      aria-hidden="true"
                    >
                      {getInitials(row.reviewer)}
                    </div>
                  )}
                  <TruncatedText className="admin-city-reviews__name">
                    {row.reviewer}
                  </TruncatedText>
                </div>
                <TruncatedText
                  as="div"
                  className="admin-city-reviews__cell"
                  role="cell"
                >
                  {row.title}
                </TruncatedText>
                <TruncatedText
                  as="div"
                  className="admin-city-reviews__cell"
                  role="cell"
                >
                  {row.date}
                </TruncatedText>
                <div className="admin-city-reviews__status-cell" role="cell">
                  <span
                    className={`admin-city-reviews__status admin-city-reviews__status--${row.status}`}
                  >
                    {STATUS_LABEL[row.status] || row.status}
                  </span>
                </div>
                <div className="admin-city-reviews__actions" role="cell">
                  <CustomButton
                    variant="transparent"
                    size="sm"
                    type="button"
                    trailingIconSrc={ICONS.arrowRight12}
                    trailingIconAlt=""
                    onClick={() => handleView(row)}
                  >
                    View Detail
                  </CustomButton>
                  {canEditReview ? (
                    <CustomIconButton
                      iconSrc={ICONS.edit}
                      label={`Edit ${row.title}`}
                      size="sm"
                      onClick={() => handleEdit(row)}
                    />
                  ) : null}
                  {canDeleteReview ? (
                    <CustomIconButton
                      iconSrc={ICONS.trash}
                      label={`Delete ${row.title}`}
                      tone="danger"
                      size="sm"
                      onClick={() => handleDelete(row)}
                    />
                  ) : null}
                </div>
              </div>
            ))}

          {!isLoading && !error && rows.length === 0 && (
            <div className="admin-city-reviews__empty">
              <EmptyState
                title="No reviews found"
                description="Try adjusting your search or filters."
              />
            </div>
          )}

          {!isLoading && error && rows.length === 0 && (
            <div className="admin-city-reviews__empty">
              <EmptyState
                title="Couldn’t load reviews"
                description={error}
                actionText="Try again"
                onActionClick={fetchReviews}
              />
            </div>
          )}
        </div>
      </div>

      <Pagination
        currentPage={currentPage}
        totalItems={totalItems}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        itemLabel="Reviews"
        prevIconSrc={ICONS.chevronLeft}
        nextIconSrc={ICONS.chevronRight}
        className="admin-city-reviews__pagination"
      />

      {canCreateReview || canEditReview ? (
        <ReviewFormModal
          isOpen={isCreateOpen || Boolean(editReviewId)}
          onClose={closeFormModal}
          onSuccess={fetchReviews}
          cityId={cityId}
          reviewId={editReviewId || undefined}
        />
      ) : null}

      <ReviewDetailModal
        isOpen={Boolean(viewReviewId)}
        onClose={() => setViewReviewId(null)}
        reviewId={viewReviewId}
        onEdit={
          canEditReview
            ? detail => {
                setViewReviewId(null)
                setEditReviewId(detail.id)
              }
            : undefined
        }
      />

      <DeleteConfirmation
        isOpen={Boolean(reviewToDelete)}
        onCancel={closeDeleteModal}
        onConfirm={handleDeleteConfirm}
        title="Delete Review"
        message={
          reviewToDelete
            ? `Are you sure you want to delete “${reviewToDelete.title}”? This action cannot be undone.`
            : ""
        }
        confirmText="Delete Review"
        cancelText="Cancel"
        isDeleting={isDeleting}
      />
    </div>
  )
}

ReviewsListing.propTypes = {
  documentTitle: PropTypes.string,
  showExport: PropTypes.bool,
  cityId: PropTypes.string,
  allowCreate: PropTypes.bool,
}

ReviewsListing.defaultProps = {
  documentTitle: "Reviews | AMOK . EARTH Admin",
  showExport: true,
  cityId: undefined,
  allowCreate: false,
}

export default ReviewsListing
